import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Eye, Code, Mail, AlertCircle, Loader2 } from 'lucide-react';
import { cn, sanitizeHtml, messageContainsEmailOrTemplate } from '@/lib/utils';
import { InsuranceAnalysisResult } from '@/types/insurance';
import CodePreview from '@/components/email-preview/CodePreview';
import InsuranceOfferPreview from '@/components/email-preview/InsuranceOfferPreview';
import InsuranceComparisonPreview from '@/components/email-preview/InsuranceComparisonPreview';
import '../styles/email-preview.css';

interface EmailPreviewProps {
  emailContent: string;
  isGenerating: boolean;
  isStreaming?: boolean;
  streamingCode?: string;
  view: 'preview' | 'code';
  onViewChange: (view: 'preview' | 'code') => void;
  isInsuranceOffer?: boolean;
  isInsuranceComparison?: boolean;
  analysisResult?: InsuranceAnalysisResult | null;
  language?: string;
  initialPrompt?: string;
  showViewToggle?: boolean;
}

type FormatOption = {
  value: string;
  label: string;
  icon: React.ReactNode;
};

const formatOptions: FormatOption[] = [
  {
    value: 'html',
    label: 'HTML',
    icon: <Code className="h-4 w-4" />
  }
];

const EmailPreview: React.FC<EmailPreviewProps> = ({
  emailContent,
  isGenerating,
  isStreaming = false,
  streamingCode = '',
  view,
  onViewChange,
  isInsuranceOffer = false,
  isInsuranceComparison = false,
  analysisResult,
  language = 'de',
  initialPrompt,
  showViewToggle = true
}) => {
  const [format, setFormat] = useState('html');
  const { toast } = useToast();
  
  useEffect(() => {
    if (isStreaming && streamingCode && view !== 'code') {
      onViewChange('code');
    }
  }, [isStreaming, streamingCode, onViewChange, view]);

  useEffect(() => {
    if (!isStreaming && streamingCode && view === 'code') {
      const timer = setTimeout(() => {
        onViewChange('preview');
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isStreaming, streamingCode, view, onViewChange]);
  
  const isValidTemplate = (content: string): boolean => {
    if (!content) return false;
    
    return (
      content.includes('<!DOCTYPE html>') || 
      (content.includes('<html') && content.includes('</html>')) ||
      (content.includes('<body') && content.includes('</body>')) ||
      (content.includes('<table') && (content.includes('<td') || content.includes('<tr')))
    );
  };
  
  const shouldShowContent = emailContent && isValidTemplate(emailContent);
  
  const codeContent = isStreaming && streamingCode ? streamingCode : emailContent;
  
  const cleanEmailContent = (content: string) => {
    if (!content) return '';
    
    const doctypeIndex = content.indexOf('<!DOCTYPE html>');
    const htmlStartIndex = content.indexOf('<html');
    const bodyStartIndex = content.indexOf('<body');
    const tableStartIndex = content.indexOf('<table');
    
    const startIndices = [doctypeIndex, htmlStartIndex, bodyStartIndex, tableStartIndex]
      .filter(index => index >= 0);
      
    if (startIndices.length > 0) {
      const firstIndex = Math.min(...startIndices);
      
      let htmlContent = content.substring(firstIndex);
      const htmlEndIndex = htmlContent.lastIndexOf('</html>');
      if (htmlEndIndex > 0) {
        htmlContent = htmlContent.substring(0, htmlEndIndex + 7);
      }
      
      return htmlContent;
    }
    
    return content;
  };

  const getLocalizedContent = () => {
    switch (language) {
      case 'fr':
        return {
          title: "Modèle d'e-mail",
          generating: "Le modèle d'e-mail est en cours de génération",
          wait: "Cela peut prendre un moment...",
          noTemplate: "Pas de modèle d'e-mail disponible",
          createNew: "Créez un nouveau modèle d'e-mail avec le chat",
          readyTitle: "Le modèle d'e-mail est prêt !",
          readyDesc: "Vous pouvez maintenant demander des modifications ou des ajustements dans le chat.",
          example: "Par exemple : \"Ajouter un logo\" ou \"Rendre le texte plus court\"."
        };
      case 'it':
        return {
          title: "Modello di email",
          generating: "Il modello di email è in fase di generazione",
          wait: "Questo potrebbe richiedere un momento...",
          noTemplate: "Nessun modello di email disponibile",
          createNew: "Crea un nuovo modello di email con la chat",
          readyTitle: "Il modello di email è pronto!",
          readyDesc: "Ora puoi chiedere modifiche o aggiustamenti nella chat.",
          example: "Ad esempio: \"Aggiungi un logo\" o \"Rendi il testo più breve\"."
        };
      default: // German (default)
        return {
          title: "E-Mail-Vorlage",
          generating: "E-Mail-Vorlage wird generiert",
          wait: "Dies kann einen Moment dauern...",
          noTemplate: "Keine E-Mail-Vorlage vorhanden",
          createNew: "Erstellen Sie mit dem Chat eine neue E-Mail-Vorlage",
          readyTitle: "Die E-Mail-Vorlage ist fertig!",
          readyDesc: "Sie können jetzt im Chat nach Änderungen oder Anpassungen fragen.",
          example: "Zum Beispiel: \"Füge ein Logo hinzu\" oder \"Mache den Text kürzer\".",
          streaming: "HTML-Template wird erstellt...",
          streamingDesc: "Bitte warten Sie während das Template generiert wird."
        };
    }
  };

  const uiText = getLocalizedContent();

  return (
    <div className="flex flex-col h-full">
      {showViewToggle && (
        <div className="border-b p-4 flex justify-between items-center bg-gray-50">
          <div className="flex items-center">
            <Mail className="h-5 w-5 text-gray-600 mr-2" />
            <span className="font-medium text-gray-700">E-Mail Vorschau</span>
          </div>
          <div className="flex space-x-2">
            <Button
              variant={view === 'preview' ? 'default' : 'outline'}
              size="sm"
              onClick={() => onViewChange('preview')}
              disabled={!emailContent && view !== 'preview'}
            >
              <Eye className="h-4 w-4 mr-1" /> Vorschau
            </Button>
            <Button
              variant={view === 'code' ? 'default' : 'outline'}
              size="sm"
              onClick={() => onViewChange('code')}
              disabled={!emailContent && !isStreaming && view !== 'code'}
            >
              <Code className="h-4 w-4 mr-1" /> Code
            </Button>
          </div>
        </div>
      )}

      <div className="flex-1 overflow-hidden">
        {isGenerating && !isStreaming ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center p-8">
              <svg
                className="animate-spin h-10 w-10 text-primary mx-auto mb-4"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <p className="text-gray-500 font-medium">{uiText.generating}</p>
              <p className="text-gray-400 text-sm mt-1">{uiText.wait}</p>
            </div>
          </div>
        ) : isStreaming && view === 'code' ? (
          <div className="flex flex-col h-full">
            <div className="bg-blue-50 border-b border-blue-200 px-4 py-2 flex items-center">
              <Loader2 className="h-4 w-4 text-blue-500 mr-2 animate-spin" />
              <span className="text-sm font-medium text-blue-700">{uiText.streaming}</span>
            </div>
            <CodePreview 
              content={streamingCode} 
              format={format} 
              formatOptions={formatOptions} 
              isStreaming={true}
            />
          </div>
        ) : view === 'preview' && shouldShowContent ? (
          <ScrollArea className="h-full">
            <div className="p-4">
              <Card className="overflow-hidden shadow-md">
                <div className="email-preview-header">
                  <h3>{uiText.title}</h3>
                  <div className="text-xs text-gray-500">
                    {language}
                  </div>
                </div>
                {isInsuranceOffer ? (
                  <InsuranceOfferPreview content={emailContent} />
                ) : isInsuranceComparison ? (
                  <InsuranceComparisonPreview content={emailContent} analysis={analysisResult} />
                ) : (
                  <div className="email-preview-container bg-white shadow-sm">
                    <div 
                      className="email-content" 
                      dangerouslySetInnerHTML={{ 
                        __html: sanitizeHtml(cleanEmailContent(emailContent))
                      }}
                    />
                  </div>
                )}
              </Card>
              
              <div className="mt-4 bg-blue-50 border border-blue-200 rounded-md p-3 flex items-start">
                <AlertCircle className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-800">{uiText.readyTitle}</p>
                  <p className="text-sm text-blue-600 mt-1">
                    {uiText.readyDesc}
                    {" "}
                    {uiText.example}
                  </p>
                </div>
              </div>
            </div>
          </ScrollArea>
        ) : view === 'code' && !isStreaming ? (
          <CodePreview 
            content={codeContent} 
            format={format} 
            formatOptions={formatOptions} 
            isStreaming={false}
          />
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center p-8">
              <Mail className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 font-medium">{uiText.noTemplate}</p>
              <p className="text-gray-400 text-sm mt-1">{uiText.createNew}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailPreview;
